import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Space } from 'antd';
import { ReloadOutlined, LoginOutlined } from '@ant-design/icons';
import { ErrorMonitor } from '@/utils/apm';

// ============================================================
// ChunkLoadError 自动刷新相关常量
// ============================================================
const CHUNK_ERROR_REFRESH_KEY = 'chunk_error_refresh_info';
const MAX_REFRESH_COUNT = 2; // 最大刷新次数
const REFRESH_WINDOW_MS = 30 * 1000; // 30秒内的刷新窗口

/**
 * 检测是否为 Chunk 加载失败错误（发版后资源丢失）
 * 常见错误信息模式：
 * - "Loading chunk xxx failed"
 * - "Failed to fetch dynamically imported module"
 * - "Unable to preload CSS"
 * - "ChunkLoadError"
 */
function isChunkLoadError(error: Error): boolean {
  const errorMessage = error.message?.toLowerCase() || '';
  const errorName = error.name?.toLowerCase() || '';

  const chunkErrorPatterns = [
    'loading chunk',
    'failed to fetch dynamically imported module',
    'unable to preload css',
    'chunkloaderror',
    'loading css chunk',
    // Vite 特有的错误模式
    'failed to load module script',
    'error loading dynamically imported module',
    'importing a module script failed',
  ];

  return (
    errorName === 'chunkloaderror' ||
    chunkErrorPatterns.some(pattern => errorMessage.includes(pattern))
  );
}

/**
 * 检查是否可以安全刷新（防止无限刷新循环）
 * 返回 true 表示可以刷新，false 表示已达到刷新上限
 */
function canSafelyRefresh(): boolean {
  try {
    const storedInfo = sessionStorage.getItem(CHUNK_ERROR_REFRESH_KEY);
    const now = Date.now();

    if (!storedInfo) {
      // 首次刷新，记录信息
      sessionStorage.setItem(CHUNK_ERROR_REFRESH_KEY, JSON.stringify({
        count: 1,
        firstRefreshTime: now,
      }));
      return true;
    }

    const info = JSON.parse(storedInfo);
    const timeSinceFirst = now - info.firstRefreshTime;

    // 如果超过刷新窗口，重置计数
    if (timeSinceFirst > REFRESH_WINDOW_MS) {
      sessionStorage.setItem(CHUNK_ERROR_REFRESH_KEY, JSON.stringify({
        count: 1,
        firstRefreshTime: now,
      }));
      return true;
    }

    // 在窗口内，检查刷新次数
    if (info.count >= MAX_REFRESH_COUNT) {
      console.warn('[SilentErrorBoundary] 已达到最大刷新次数，停止自动刷新');
      return false;
    }

    // 增加刷新计数
    sessionStorage.setItem(CHUNK_ERROR_REFRESH_KEY, JSON.stringify({
      count: info.count + 1,
      firstRefreshTime: info.firstRefreshTime,
    }));
    return true;
  } catch (e) {
    // sessionStorage 不可用时，允许刷新一次
    console.error('[SilentErrorBoundary] sessionStorage 操作失败:', e);
    return true;
  }
}

/**
 * 清除刷新计数（页面正常加载后调用）
 */
function clearRefreshCount(): void {
  try {
    sessionStorage.removeItem(CHUNK_ERROR_REFRESH_KEY);
  } catch (e) {
    // 忽略错误
  }
}

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  fallback?: ReactNode;
  /** 功能区标识（如 'canvas'），随异常上报进 extra，供异常看板按 feature 筛选 */
  feature?: string;
  /** 边界层级（如 'page'），随异常上报进 extra，标注该边界捕获的粒度 */
  level?: string;
}

interface State {
  hasError: boolean;
  isHandling: boolean;
  isReloading: boolean;
  isRedirecting: boolean;
  isChunkError: boolean; // 是否为 ChunkLoadError，用于跳过错误页面直接刷新
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * 静默错误边界组件
 * 捕获子组件树中的 JavaScript 错误，不显示错误信息给用户
 * 而是执行回调函数（如清除数据并跳转到登录页）
 */
class SilentErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isHandling: false,
      isReloading: false,
      isRedirecting: false,
      isChunkError: false,
      error: undefined,
      errorInfo: undefined,
    };

    // 页面成功加载，清除 ChunkLoadError 刷新计数
    // 说明之前的自动刷新成功获取到了新资源
    clearRefreshCount();
  }

  // 刷新页面
  handleReload = () => {
    this.setState({ isReloading: true });
    // 延迟一下，让用户看到加载状态
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  // 重新登录：清除数据并跳转到登录页
  handleReLogin = async () => {
    try {
      this.setState({ isRedirecting: true });
      
      // 使用统一的清除数据并跳转登录函数
      const { clearDataAndRedirectToLogin } = await import('@/utils/auth');
      const currentPath = window.location.pathname + window.location.search;
      await clearDataAndRedirectToLogin(currentPath);
    } catch (err) {
      console.error('[SilentErrorBoundary] 重新登录处理失败:', err);
      // 如果清除数据失败，至少跳转到登录页
      const { redirectToLoginWithoutNavigate } = await import('@/utils/auth');
      redirectToLoginWithoutNavigate();
    }
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // 检测是否为 ChunkLoadError（发版后资源丢失）
    const chunkError = isChunkLoadError(error);

    // 如果是 ChunkLoadError 且可以安全刷新，标记为 isChunkError
    // 这样 render 时不会显示错误页面，而是直接刷新
    if (chunkError && canSafelyRefresh()) {
      return {
        hasError: true,
        isHandling: true,
        isChunkError: true, // 标记为 chunk 错误，render 时跳过错误页面
        isReloading: false,
        isRedirecting: false,
        error: error,
      };
    }

    // 普通错误或已达到刷新上限，显示错误页面
    return {
      hasError: true,
      isHandling: true,
      isChunkError: false,
      isReloading: false,
      isRedirecting: false,
      error: error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 保存errorInfo到状态
    this.setState({ errorInfo });

    // 记录错误日志
    console.error('[SilentErrorBoundary] 捕获到错误:', error, errorInfo);

    // ============================================================
    // ChunkLoadError 处理：直接刷新页面获取最新资源
    // isChunkError 在 getDerivedStateFromError 中已判断并设置
    // ============================================================
    if (this.state.isChunkError) {
      console.warn('[SilentErrorBoundary] 检测到 ChunkLoadError，可能是发版后资源更新导致');

      // 上报 ChunkLoadError 到 APM（标记为 warn 级别，因为会自动恢复）
      try {
        ErrorMonitor.trackError(error, {
          component: 'SilentErrorBoundary',
          errorType: 'ChunkLoadError',
          page: window.location.pathname,
          url: window.location.href,
          action: 'auto_refresh',
        }, 'warn');
      } catch (e) {
        // 忽略 APM 上报错误
      }

      console.log('[SilentErrorBoundary] 执行自动刷新以获取最新资源...');
      // 立即刷新，不显示任何错误页面
      window.location.reload();
      return; // 不再继续执行后续逻辑
    }

    // 如果是 ChunkLoadError 但 isChunkError 为 false，说明已达到刷新上限
    if (isChunkLoadError(error)) {
      console.error('[SilentErrorBoundary] ChunkLoadError 已达到最大刷新次数，显示错误页面');
    }

    // 上报错误到 APM
    try {
      // 构建错误上下文。注意：此 extra 走 captureException 的 payload.extra（异常详情元数据），
      // 与 sendLog 的 extra（字符串会被转成 Category 维度）机制不同，不会打爆 Top20 维度。
      // 故只保留「增量信息」字段；errorStack/errorMessage/componentStack 等已由 error 对象本身、
      // 以及下方第四参 react.componentStack 承载，不再重复塞入。
      const errorContext: Record<string, string> = {
        component: 'SilentErrorBoundary',
        page: window.location.pathname,
        url: window.location.href,
        userAgent: navigator.userAgent,
        errorName: error.name,
        feature: this.props.feature ?? 'other',
        boundaryLevel: this.props.level ?? 'unknown',
      };

      // 使用 ErrorMonitor 上报错误；componentStack 走第四参，落异常看板的 react.componentStack 字段
      ErrorMonitor.trackError(error, errorContext, 'error', {
        version: React.version,
        componentStack: errorInfo.componentStack || '',
      });

      console.log('[SilentErrorBoundary] 错误已上报到 APM');
    } catch (apmError) {
      // APM 上报失败不应该影响错误边界的功能
      console.error('[SilentErrorBoundary] APM 错误上报失败:', apmError);
    }

    // 执行错误回调
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (callbackError) {
        console.error('[SilentErrorBoundary] 错误回调执行失败:', callbackError);
      }
    }
  }

  // 高亮错误堆栈中的文件路径和行号(仅标红第一个带时间戳的文件)
  private highlightStackTrace(stack: string): ReactNode {
    const lines = stack.split('\n');

    // 过滤规则:
    // 1. 排除包含 node_modules 的行
    // 2. 仅保留包含文件路径和行号的行(格式: .tsx/.ts/.jsx/.js[?查询参数]:行号:列号)
    const filteredLines = lines.filter(line => {
      if (line.includes('node_modules')) return false;
      // 只保留有实际文件路径的行,过滤掉 "at div"、"at App" 等无路径的行
      // 支持 Vite 热更新时间戳: ConfigPanel.tsx?t=1770097193036:101:5
      return /\.(tsx?|jsx?|js|ts)(\?[^:]*)?:\d+:\d+/.test(line);
    });

    // 如果过滤后没有内容,显示原始堆栈(兜底方案)
    const linesToRender = filteredLines.length > 0 ? filteredLines : lines;

    // 找到第一个带时间戳的行
    let firstTimestampLine: string | null = null;
    for (const line of linesToRender) {
      if (line.includes('?t=') || line.includes('?v=')) {
        firstTimestampLine = line;
        break;
      }
    }

    return linesToRender.map((line, index) => {
      // 匹配文件路径和行号的正则表达式
      // 支持 Vite 时间戳: ConfigPanel.tsx?t=1770097193036:101:5
      const filePathRegex = /(\()?([^()]+\.(tsx?|jsx?|js|ts)(?:\?[^:)]*)?):(\d+):(\d+)\)?/g;

      const parts: ReactNode[] = [];
      let lastIndex = 0;
      let match;

      while ((match = filePathRegex.exec(line)) !== null) {
        // 添加匹配前的文本
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }

        const fullMatch = match[0];
        const filePath = match[2];
        const lineNumber = match[4];
        const columnNumber = match[5];

        // 判断当前行是否是第一个带时间戳的文件
        const isFirstTimestamp = firstTimestampLine !== null && line === firstTimestampLine;

        // 只有第一个带时间戳的文件才标红高亮
        const filePathStyle = isFirstTimestamp
          ? { color: '#ff6b6b', fontWeight: 'bold' as const }
          : { color: '#ffffff' }; // 其他文件白色显示

        const lineNumberStyle = isFirstTimestamp
          ? { color: '#ff6b6b' }
          : { color: '#ffffff' };

        // 高亮文件路径和行号
        parts.push(
          <span key={`${index}-${match.index}`}>
            {match[1] || ''} {/* 左括号(如果有) */}
            <span style={filePathStyle}>
              {filePath}
            </span>
            <span style={lineNumberStyle}>
              :{lineNumber}:{columnNumber}
            </span>
            {fullMatch.endsWith(')') ? ')' : ''}
          </span>
        );

        lastIndex = filePathRegex.lastIndex;
      }

      // 添加剩余的文本
      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }

      return (
        <div key={index}>
          {parts.length > 0 ? parts : line}
        </div>
      );
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // ============================================================
      // ChunkLoadError：显示版本更新提示，不显示错误页面
      // componentDidCatch 中会执行刷新，用户体验友好
      // ============================================================
      if (this.state.isChunkError) {
        return null
        // return (
        //   <div
        //     style={{
        //       display: 'flex',
        //       flexDirection: 'column',
        //       alignItems: 'center',
        //       justifyContent: 'center',
        //       height: '100vh',
        //       width: '100vw',
        //       position: 'fixed',
        //       top: 0,
        //       left: 0,
        //       background: '#0a0a0a',
        //       zIndex: 9999,
        //     }}
        //   >
        //     {/* 加载动画点 */}
        //     <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        //       {[0, 1, 2].map((i) => (
        //         <div
        //           key={i}
        //           style={{
        //             width: '12px',
        //             height: '12px',
        //             borderRadius: '50%',
        //             background: 'linear-gradient(123deg, #feae45 19.66%, #ff6d49 67.36%)',
        //             animation: 'chunkErrorPulse 1.4s ease-in-out infinite',
        //             animationDelay: `${i * 0.2}s`,
        //           }}
        //         />
        //       ))}
        //     </div>
        //     {/* 提示文字 */}
        //     <div
        //       style={{
        //         color: 'rgba(255, 255, 255, 0.75)',
        //         fontSize: '14px',
        //         fontWeight: 400,
        //         textAlign: 'center',
        //       }}
        //     >
        //       检测到新版本，正在更新...
        //     </div>
        //     {/* 进度条 */}
        //     <div
        //       style={{
        //         width: '200px',
        //         height: '2px',
        //         background: 'rgba(255, 255, 255, 0.1)',
        //         borderRadius: '1px',
        //         marginTop: '16px',
        //         overflow: 'hidden',
        //       }}
        //     >
        //       <div
        //         style={{
        //           height: '100%',
        //           background: 'linear-gradient(123deg, #feae45 19.66%, #ff6d49 67.36%)',
        //           borderRadius: '1px',
        //           animation: 'chunkErrorProgress 1.5s ease-in-out infinite',
        //         }}
        //       />
        //     </div>
        //     {/* CSS 动画 */}
        //     <style>{`
        //       @keyframes chunkErrorPulse {
        //         0%, 100% { opacity: 0.4; }
        //         50% { opacity: 1; }
        //       }
        //       @keyframes chunkErrorProgress {
        //         0% { width: 0%; transform: translateX(0); }
        //         50% { width: 100%; transform: translateX(0); }
        //         100% { width: 100%; transform: translateX(100%); }
        //       }
        //     `}</style>
        //   </div>
        // );
      }

      // 检测是否为开发环境
      const isDevelopment = import.meta.env.MODE === 'development' ||
                           import.meta.env.DEV === true ||
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';

      // 如果提供了自定义 fallback，使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 开发环境：显示详细错误信息
      if (isDevelopment && this.state.error) {
        return (
          <div
            style={{
              height: '100vh',
              width: '100vw',
              overflow: 'auto',
              backgroundColor: '#1a1a1a',
              color: '#ffffff',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px 20px',
                minHeight: '100%',
              }}
            >
              <div style={{ maxWidth: '1200px', width: '100%' }}>
              {/* 标题 */}
              <div style={{ marginBottom: '32px', textAlign: 'center' }}>
                <h1 style={{
                  fontSize: '32px',
                  color: '#ff6b6b',
                  marginBottom: '12px',
                  fontWeight: 700,
                }}>
                  ⚠️ 开发环境错误
                </h1>
                <p style={{ fontSize: '16px', color: '#a0a0a0' }}>
                  以下错误信息仅在开发环境显示，生产环境将显示友好的错误页面
                </p>
              </div>

              {/* 错误信息卡片 */}
              <div
                style={{
                  backgroundColor: '#2a2a2a',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  border: '2px solid #ff6b6b',
                }}
              >
                <h2 style={{
                  fontSize: '20px',
                  color: '#ff6b6b',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <span>🔴</span>
                  错误类型: {this.state.error.name || 'Error'}
                </h2>
                <div
                  style={{
                    backgroundColor: '#1a1a1a',
                    padding: '16px',
                    borderRadius: '8px',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    overflowX: 'auto',
                    marginBottom: '16px',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {this.state.error.message}
                  </pre>
                </div>
              </div>

              {/* 错误堆栈 */}
              {this.state.error.stack && (
                <div
                  style={{
                    backgroundColor: '#2a2a2a',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px',
                  }}
                >
                  <h2 style={{
                    fontSize: '18px',
                    color: '#ffa726',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span>📍</span>
                    错误堆栈
                  </h2>
                  <div
                    style={{
                      backgroundColor: '#1a1a1a',
                      padding: '16px',
                      borderRadius: '8px',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      overflowX: 'auto',
                      maxHeight: '400px',
                      overflowY: 'auto',
                    }}
                  >
                    <div style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {this.highlightStackTrace(this.state.error.stack)}
                    </div>
                  </div>
                </div>
              )}

              {/* 组件堆栈 */}
              {this.state.errorInfo?.componentStack && (
                <div
                  style={{
                    backgroundColor: '#2a2a2a',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px',
                  }}
                >
                  <h2 style={{
                    fontSize: '18px',
                    color: '#66bb6a',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span>🔗</span>
                    React 组件堆栈
                  </h2>
                  <div
                    style={{
                      backgroundColor: '#1a1a1a',
                      padding: '16px',
                      borderRadius: '8px',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      overflowX: 'auto',
                      maxHeight: '400px',
                      overflowY: 'auto',
                    }}
                  >
                    <div style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {this.highlightStackTrace(this.state.errorInfo.componentStack)}
                    </div>
                  </div>
                </div>
              )}

              {/* 操作按钮 */}
              <div style={{ textAlign: 'center', marginTop: '32px' }}>
                <Space size="large">
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={this.handleReload}
                    loading={this.state.isReloading}
                    size="large"
                    style={{
                      height: '44px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #feae45 0%, #ff6d49 100%)',
                      border: 'none',
                      color: '#fff',
                      paddingLeft: '28px',
                      paddingRight: '28px',
                      fontSize: '15px',
                      fontWeight: 600,
                      boxShadow: '0 4px 16px rgba(255, 109, 73, 0.3)',
                    }}
                  >
                    刷新页面
                  </Button>
                  <Button
                    type="default"
                    icon={<LoginOutlined />}
                    onClick={this.handleReLogin}
                    loading={this.state.isRedirecting}
                    size="large"
                    style={{
                      height: '44px',
                      borderRadius: '10px',
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.85)',
                      paddingLeft: '28px',
                      paddingRight: '28px',
                      fontSize: '15px',
                      fontWeight: 600,
                    }}
                  >
                    重新登录
                  </Button>
                </Space>
              </div>

              {/* 提示信息 */}
              <div style={{
                marginTop: '32px',
                textAlign: 'center',
                padding: '16px',
                backgroundColor: 'rgba(66, 165, 245, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(66, 165, 245, 0.3)',
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#64b5f6',
                  lineHeight: '1.6',
                }}>
                  💡 提示：这是开发环境特有的详细错误展示。<br />
                  在生产环境中，用户将看到友好的错误提示页面，而不是这些技术细节。
                </p>
              </div>
            </div>
          </div>
          </div>
        );
      }

      // 生产环境：显示友好的错误提示
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#121212',
            color: '#ffffff',
          }}
        >
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <div style={{ marginBottom: '24px', fontSize: '16px', color: '#ffffff' }}>
              <h2>抱歉，遇到了一些问题</h2>
              <p>我们已经记录了这个问题，技术团队会尽快修复。请选择以下操作：</p>
            </div>
            <Space size="large">
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
                loading={this.state.isReloading}
                style={{
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #feae45 0%, #ff6d49 100%)',
                  border: 'none',
                  color: '#fff',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  boxShadow: '0 4px 16px rgba(255, 109, 73, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ff6d49 0%, #feae45 100%)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 109, 73, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #feae45 0%, #ff6d49 100%)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(255, 109, 73, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                刷新页面
              </Button>
              <Button
                type="default"
                icon={<LoginOutlined />}
                onClick={this.handleReLogin}
                loading={this.state.isRedirecting}
                style={{
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.85)',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = '#ffffff';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                }}
              >
                重新登录
              </Button>
            </Space>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SilentErrorBoundary;

